import React, { useState } from 'react';

const PHASES = [
  {
    id: 'navigate',
    name: 'Navigate',
    tagline: 'Charting Your Succession Course',
    color: 'from-blue-600 to-blue-700',
    borderColor: 'border-blue-600',
    bgColor: 'bg-blue-50',
    components: [
      'Assess current organizational state and strategic direction',
      'Define future leadership requirements and competencies',
      'Identify critical roles beyond the CEO position',
      'Establish succession planning governance and accountability',
      'Create a timeline and roadmap for the succession process',
      'Engage stakeholders and communicate the succession planning initiative',
      'Align succession planning with the credit union\'s strategic plan'
    ]
  },
  {
    id: 'evaluate',
    name: 'Evaluate',
    tagline: 'Assessing Leadership Potential',
    color: 'from-teal-600 to-teal-700',
    borderColor: 'border-teal-600',
    bgColor: 'bg-teal-50',
    components: [
      'Implement competency-based assessment frameworks',
      'Evaluate technical skills, leadership capabilities, and strategic thinking',
      'Assess cultural fit and alignment with credit union values',
      'Identify internal candidates with leadership potential',
      'Conduct 360-degree feedback and performance reviews',
      'Determine readiness levels and development gaps',
      'Consider external candidates when appropriate',
      'Document evaluation criteria and results objectively'
    ]
  },
  {
    id: 'xccelerate',
    name: 'Xccelerate',
    tagline: 'Developing Future Leaders',
    color: 'from-purple-600 to-purple-700',
    borderColor: 'border-purple-600',
    bgColor: 'bg-purple-50',
    components: [
      'Create individualized development plans based on evaluation results',
      'Provide stretch assignments and cross-functional experiences',
      'Implement mentoring and coaching programs',
      'Facilitate executive education and professional development',
      'Build strategic thinking and decision-making capabilities',
      'Develop board presentation and stakeholder management skills',
      'Create opportunities for external networking and industry engagement',
      'Monitor progress and adjust development plans as needed',
      'Ensure candidates gain exposure to all aspects of credit union operations'
    ]
  },
  {
    id: 'transition',
    name: 'Transition',
    tagline: 'Managing the Leadership Change',
    color: 'from-orange-600 to-orange-700',
    borderColor: 'border-orange-600',
    bgColor: 'bg-orange-50',
    components: [
      'Develop comprehensive knowledge transfer plans',
      'Create communication strategies for internal and external stakeholders',
      'Establish transition timelines and milestones',
      'Facilitate relationship building with board, regulators, and key partners',
      'Manage the outgoing leader\'s departure with dignity and appreciation',
      'Support the incoming leader through structured onboarding',
      'Maintain organizational stability during the transition period',
      'Monitor early performance and provide ongoing support',
      'Celebrate successes and honor the legacy of departing leaders'
    ]
  }
];

const PhaseCard = ({ phase, isExpanded, onToggle }) => {
  return (
    <div className={`border-l-4 ${phase.borderColor} rounded-lg overflow-hidden shadow-sm mb-4`}>
      <button
        onClick={onToggle}
        className={`w-full ${phase.bgColor} px-6 py-4 flex items-center justify-between hover:opacity-90 transition-opacity`}
      >
        <div className="flex items-center gap-4">
          <span className={`bg-gradient-to-r ${phase.color} text-white text-xl font-extrabold w-10 h-10 rounded-full flex items-center justify-center`}>
            {phase.name.charAt(0)}
          </span>
          <div className="text-left">
            <h3 className="font-bold text-gray-900 text-base md:text-lg">{phase.name}</h3>
            <p className="text-gray-600 text-base">{phase.tagline}</p>
          </div>
        </div>
        <svg
          className={`w-5 h-5 text-gray-500 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isExpanded && (
        <div className="bg-white px-6 py-4">
          <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Key Components</h4>
          <ul className="space-y-2">
            {phase.components.map((component, i) => (
              <li key={i} className="flex items-start gap-3 text-gray-700">
                <svg className={`w-5 h-5 flex-shrink-0 mt-0.5 text-${phase.borderColor.replace('border-', '')}`} fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span>{component}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

const NextMethodOverview = () => {
  const [expandedPhases, setExpandedPhases] = useState({});

  const togglePhase = (phaseId) => {
    setExpandedPhases(prev => ({
      ...prev,
      [phaseId]: !prev[phaseId]
    }));
  };

  const expandAll = () => {
    const allExpanded = {};
    PHASES.forEach(phase => {
      allExpanded[phase.id] = true;
    });
    setExpandedPhases(allExpanded);
  };

  const collapseAll = () => {
    setExpandedPhases({});
  };

  const allExpanded = PHASES.every(phase => expandedPhases[phase.id]);

  return (
    <section className="py-12 md:py-20 bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-4 md:mb-6 font-PlusJakartaSans">
            The NEXT Method
          </h2>

          {/* Introduction */}
          <div className="text-base md:text-lg text-gray-600 leading-relaxed font-Inter mb-8">
            <p className="mb-4">
              The NEXT Method transforms succession planning from a reactive crisis into a strategic advantage.
              Rather than waiting until a leader announces their departure, this framework enables credit unions
              to build continuous leadership pipelines that ensure organizational stability and growth.
            </p>
          </div>

          {/* Expand/Collapse Controls */}
          <div className="flex justify-end mb-4">
            <button
              onClick={allExpanded ? collapseAll : expandAll}
              className="text-sm text-dark-teal-600 hover:text-dark-dark-teal-400 font-medium hover:underline transition-all"
            >
              {allExpanded ? 'Collapse All' : 'Expand All'}
            </button>
          </div>

          {/* Phase Cards */}
          <div>
            {PHASES.map(phase => (
              <PhaseCard
                key={phase.id}
                phase={phase}
                isExpanded={expandedPhases[phase.id]}
                onToggle={() => togglePhase(phase.id)}
              />
            ))}
          </div>

          {/* Conclusion */}
          <div className="bg-gradient-to-r from-gray-800 to-gray-900 rounded-xl p-6 mt-8 text-white">
            <p className="text-center">
              <strong>Remember:</strong> The NEXT Method is a continuous cycle, not a one-time project.
              Effective succession planning requires ongoing attention, regular updates, and commitment
              from leadership at all levels.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default NextMethodOverview;
